const number_or_null = (value) => {
    if (value === null || value === undefined || `${value}`.trim() === '') {
        return null;
    }

    const parsedValue = Number(value);

    if (Number.isFinite(parsedValue)) {
        return parsedValue;
    }

    return null;
};

const first_number = (values = []) => {
    return values
        .map((value) => number_or_null(value))
        .find((value) => value !== null);
};

export const offer_total_value = (priceData = {}) => {
    const directTotal = first_number([
        priceData?.total,
        priceData?.totalWithVat
    ]);

    if (directTotal !== undefined) {
        return directTotal;
    }

    const recurringTotal = first_number([
        priceData?.recurringTotalWithVat,
        priceData?.recurringNetAmount,
        priceData?.mixedRecurringManualNetAmount,
        priceData?.mixedRecurringBaseNetAmount
    ]);
    const oneOffTotal = first_number([
        priceData?.oneOffTotalWithVat,
        priceData?.oneOffNetAmount,
        priceData?.mixedOneOffManualNetAmount,
        priceData?.mixedOneOffBaseNetAmount
    ]);

    if (recurringTotal !== undefined || oneOffTotal !== undefined) {
        return (recurringTotal || 0) + (oneOffTotal || 0);
    }

    const fallbackTotal = first_number([
        priceData?.totalNetAmount,
        priceData?.subtotal
    ]);

    return fallbackTotal || 0;
};

export const offer_total_is_positive = (priceData = {}) => {
    return offer_total_value(priceData) > 0;
};

export const draft_save_next_step = (savedOfferDraft, nextStep) => {
    if (!savedOfferDraft) {
        return null;
    }

    return nextStep;
};

export const handoff_prefill_should_apply = (message = {}, expectedHandoffId = '', acceptedHandoffId = '') => {
    const incomingHandoffId = `${message?.handoffId || ''}`.trim();
    const normalizedExpectedHandoffId = `${expectedHandoffId || ''}`.trim();
    const normalizedAcceptedHandoffId = `${acceptedHandoffId || ''}`.trim();

    if (message?.type !== 'godfather_offer_prefill') {
        return false;
    }

    if (!normalizedExpectedHandoffId.length) {
        return false;
    }

    if (incomingHandoffId !== normalizedExpectedHandoffId) {
        return false;
    }

    if (normalizedAcceptedHandoffId === normalizedExpectedHandoffId) {
        return false;
    }

    return true;
};

export const accepted_member_can_start_agency_offer = (isAcceptedMember = false, hasValidAgencyHandoff = false, isSalesMember = false) => {
    return isAcceptedMember === true && isSalesMember === true && hasValidAgencyHandoff === true;
};

export const accepted_member_can_manage_agency_preoffer = (permissions = {}) => {
    return permissions?.canAccessInternalPages === true;
};

export const new_offer_main_loader_is_visible = (loadingState = {}) => {
    if (loadingState?.hasDraftError) {
        return false;
    }

    return Boolean(
        loadingState?.handoffIsWaiting
        || loadingState?.isDraftLoading
        || loadingState?.isDraftSaving
    );
};

export const preoffer_session_uses_legacy_workflow = (error = {}) => {
    return error?.response?.status === 409
        && error?.response?.data?.message === 'This offer uses the legacy Preoffer workflow.';
};

export const preoffer_coordinator_can_continue = (permissions = {}, isCoordinatorTurnMember = false) => {
    return Boolean(
        isCoordinatorTurnMember
        || permissions?.isCompanyHead
        || permissions?.isGrowthDepartmentHead
        || permissions?.isSalesTeamLeader
    );
};

export const legacy_request_offer_setup_mode = (typesData = {}) => {
    return typesData?.requestOfferMode === true;
};

export const request_offer_setup_mode = (freshRequestOfferMode = false, typesData = {}) => {
    return freshRequestOfferMode === true || legacy_request_offer_setup_mode(typesData);
};

const normalized_key = (value = '') => {
    return `${value || ''}`.trim().toLowerCase().replace(/[\s-]+/g, '_');
};

export const offer_catalog_type_key = (typeName = '') => {
    const normalizedType = normalized_key(typeName);

    return normalizedType === 'creative' ? 'graphic' : normalizedType;
};

export const fresh_offer_selected_types = (requestOfferMode = false) => {
    return [];
};

export const initial_offer_selected_types = (typesData = {}, requestOfferMode = false, templateType = '') => {
    const normalizeType = (typeName = '') => {
        const catalogType = offer_catalog_type_key(typeName);
        return catalogType === 'website' ? 'website_creation' : catalogType;
    };
    const supportedTypes = ['marketing', 'graphic', 'website_creation', 'website_maintenance', 'sass'];
    const storedTypes = (Array.isArray(typesData?.selectedTypes) ? typesData.selectedTypes : [])
        .map(normalizeType)
        .filter((typeName) => supportedTypes.includes(typeName));

    if (storedTypes.length) {
        return [storedTypes[0]];
    }

    if (requestOfferMode) {
        return [];
    }

    if (['niffler', 'niffler_prospect'].includes(normalized_key(typesData?.sourceSystem))) {
        return ['marketing'];
    }

    const normalizedTemplateType = normalizeType(templateType);

    if (supportedTypes.includes(normalizedTemplateType)) {
        return [normalizedTemplateType];
    }

    return fresh_offer_selected_types(requestOfferMode);
};

export const niffler_brand_has_active_quests = (typesData = {}) => {
    const activeQuestCount = Number(
        typesData?.sourceBrandQuestCounts?.active
        ?? typesData?.sourceBrandActiveQuestCount
        ?? 0
    );

    return typesData?.sourceBrandHasActiveQuests === true
        || (Number.isFinite(activeQuestCount) && activeQuestCount > 0);
};

export const offer_type_is_available = (typeName = '') => {
    const normalizedType = offer_catalog_type_key(typeName);

    return ['marketing', 'graphic', 'website_creation', 'website_maintenance'].includes(normalizedType);
};

export const marketing_request_required = (selectedTypes = []) => {
    return selectedTypes.some((typeName) => normalized_key(typeName) === 'marketing');
};

export const request_client_should_reset_on_type_change = (selectedTypes = [], nextType = '') => {
    return marketing_request_required(selectedTypes)
        || normalized_key(nextType) === 'marketing';
};

export const logo_manager_review_required = (selectedServices = {}) => {
    const logoServiceKeys = [
        'logo',
        'logos',
        'graphic_logo',
        'graphic_logos',
        'graphic_design_logo',
        'graphic_design_logos',
        'creative_logo',
        'creative_logos'
    ];

    return logoServiceKeys.some((serviceKey) => selectedServices?.[serviceKey] === true);
};

export const graphic_manager_review_required = (selectedTypes = []) => {
    return selectedTypes.some((typeName) => {
        return ['graphic', 'creative'].includes(normalized_key(typeName));
    });
};

export const mandatory_help_review_required = (selectedTypes = [], selectedServices = {}) => {
    return website_manager_review_required(selectedTypes)
        || graphic_manager_review_required(selectedTypes)
        || logo_manager_review_required(selectedServices);
};

export const website_manager_review_required = (selectedTypes = []) => {
    return selectedTypes.some((typeName) => {
        return ['website', 'website_creation', 'website_maintenance'].includes(normalized_key(typeName));
    });
};

export const preoffer_department_estimate_turn = (workflowMode = '', preofferTurn = '') => {
    const normalizedWorkflowMode = normalized_key(workflowMode);

    if (normalizedWorkflowMode === 'help_department' || normalizedWorkflowMode === 'request_department') {
        return true;
    }

    if (
        normalizedWorkflowMode === 'help_growth'
        || normalizedWorkflowMode === 'help_done'
        || normalizedWorkflowMode === 'request_growth'
        || normalizedWorkflowMode === 'request_done'
    ) {
        return false;
    }

    return [
        'team_lead',
        'teamleader',
        'team_leader',
        'teamlead'
    ].includes(normalized_key(preofferTurn));
};

export const preoffer_growth_estimate_turn = (workflowMode = '', preofferTurn = '') => {
    const normalizedWorkflowMode = normalized_key(workflowMode);

    if (normalizedWorkflowMode === 'help_growth' || normalizedWorkflowMode === 'request_growth') {
        return true;
    }

    if (
        normalizedWorkflowMode === 'help_department'
        || normalizedWorkflowMode === 'help_done'
        || normalizedWorkflowMode === 'request_department'
        || normalizedWorkflowMode === 'request_done'
    ) {
        return false;
    }

    return normalized_key(preofferTurn) === 'coordinator';
};

export const preoffer_department_board_from_types = (selectedTypes = []) => {
    const normalizedTypes = selectedTypes.map((typeItem) => normalized_key(typeItem)).map((typeKey) => {
        if (typeKey === 'graphic') {
            return 'creative';
        }

        return typeKey;
    });
    const matchedBoard = normalizedTypes.map((typeKey) => {
        if (typeKey === 'website' || typeKey === 'website_creation' || typeKey === 'website_maintenance') {
            return 'website';
        }

        if (typeKey === 'marketing' || typeKey === 'creative') {
            return 'marketing';
        }

        return '';
    }).find((boardValue) => boardValue.length);

    return matchedBoard || '';
};

const read_offer_origin = (offerDraft = {}) => {
    const originValue = normalized_key(offerDraft?.origin || offerDraft?.workflow?.origin);
    const offerCode = `${offerDraft?.ofrId || offerDraft?.offerCode || offerDraft?.typesData?.offerCode || ''}`.trim().toUpperCase();

    if (offerCode.startsWith('H-OFR-')) {
        return 'help';
    }

    if (offerCode.startsWith('R-OFR-')) {
        return 'request';
    }

    return originValue;
};

const read_preoffer_turn = (offerDraft = {}) => {
    return normalized_key(
        offerDraft?.typesData?.preofferTurn
        || offerDraft?.setupData?.preofferTurn
        || offerDraft?.workflow?.turn
    );
};

const read_has_help_estimate = (offerDraft = {}) => {
    return Boolean(
        `${offerDraft?.typesData?.preofferEstimateSubmittedAt || ''}`.trim()
        || `${offerDraft?.setupData?.preofferEstimateSubmittedAt || ''}`.trim()
        || `${offerDraft?.workflow?.estimateSubmittedAt || ''}`.trim()
    );
};

export const offer_workflow_mode = (offerDraft = {}) => {
    const origin = read_offer_origin(offerDraft);
    const turn = read_preoffer_turn(offerDraft);
    const workflowVersion = Number(offerDraft?.workflow?.version || offerDraft?.setupData?.preofferWorkflowVersion || 0);

    if (workflowVersion === 2) {
        return turn === 'done' ? 'preoffer_v2_done' : `preoffer_v2_${turn || 'preparer'}`;
    }
    const directSalesRequest = origin === 'request'
        && normalized_key(offerDraft?.workflow?.route) === 'request_to_sales';
    const selectedTypes = [
        offerDraft?.selectedType,
        ...(Array.isArray(offerDraft?.selectedTypes) ? offerDraft.selectedTypes : []),
        ...(Array.isArray(offerDraft?.typesData?.selectedTypes) ? offerDraft.typesData.selectedTypes : [])
    ].filter(Boolean);
    const websiteRequest = origin === 'request' && (
        website_manager_review_required(selectedTypes)
        || normalized_key(
            offerDraft?.workflow?.preofferDepartmentBoard
            || offerDraft?.typesData?.preofferDepartmentBoard
            || offerDraft?.setupData?.preofferDepartmentBoard
        ) === 'website'
    );

    if (directSalesRequest) {
        return turn === 'done' ? 'request_done' : 'request_growth';
    }

    if (websiteRequest) {
        if (turn === 'done') {
            return 'request_done';
        }

        if (turn === 'coordinator' || read_has_help_estimate(offerDraft)) {
            return 'request_growth';
        }

        return 'request_department';
    }

    if (origin !== 'help') {
        return 'standard';
    }

    if (turn === 'done') {
        return 'help_done';
    }

    if (turn === 'coordinator' || read_has_help_estimate(offerDraft)) {
        return 'help_growth';
    }

    return 'help_department';
};

const help_estimate_setup_fields = [
    'selectedServices',
    'serviceSuggestedPricingValues',
    'serviceComments',
    'eshopQuestionAnswers',
    'starterQuestionAnswers',
    'businessQuestionAnswers',
    'eshopProjectText'
];

export const help_estimate_setup_payload = (setupData = {}) => {
    return help_estimate_setup_fields.reduce((payload, fieldName) => {
        if (setupData?.[fieldName] === undefined) {
            return payload;
        }

        return {
            ...payload,
            [fieldName]: setupData[fieldName]
        };
    }, {});
};

export const reviewer_suggested_pricing_is_complete = (selectedServices = {}, suggestedPricingValues = {}) => {
    const selectedServiceKeys = Object.entries(selectedServices || {})
        .filter(([, selected]) => selected === true)
        .map(([serviceKey]) => serviceKey);

    if (!selectedServiceKeys.length) {
        return false;
    }

    return selectedServiceKeys.every((serviceKey) => {
        const rawValue = suggestedPricingValues?.[serviceKey];
        const numericValue = Number(rawValue);

        return `${rawValue ?? ''}`.trim().length > 0
            && Number.isFinite(numericValue)
            && numericValue > 0;
    });
};

const offer_should_open_manager_review = (offerDraft = {}) => {
    return ['help_department', 'request_department'].includes(offer_workflow_mode(offerDraft));
};

export const continue_offer_start_step = (continueOfferMode = false, offerDraft = {}) => {
    if (!offerDraft?.offerId) {
        return 0;
    }

    if (Number(offerDraft?.workflow?.version || offerDraft?.setupData?.preofferWorkflowVersion || 0) === 2) {
        return read_preoffer_turn(offerDraft) === 'preparer' ? 1 : 2;
    }

    if (offer_should_open_manager_review(offerDraft)) {
        return 1;
    }

    if (['help_growth', 'request_growth'].includes(offer_workflow_mode(offerDraft))) {
        return 2;
    }

    if (!continueOfferMode) {
        return 0;
    }

    return 2;
};

export const legacy_preoffer_issue_review_state = (offerDraft = {}) => {
    const workflowVersion = Number(offerDraft?.workflow?.version || offerDraft?.setupData?.preofferWorkflowVersion || 0);
    const managerBoard = normalized_key(
        offerDraft?.workflow?.preofferDepartmentBoard
        || offerDraft?.setupData?.preofferDepartmentBoard
        || offerDraft?.typesData?.preofferDepartmentBoard
    );
    const originalWorkflowMode = offer_workflow_mode(offerDraft);
    const enabled = offerDraft?.legacyPreofferIssueContinuation === true
        && workflowVersion !== 2
        && ['marketing', 'website'].includes(managerBoard)
        && (originalWorkflowMode.startsWith('help_') || originalWorkflowMode.startsWith('request_'));

    if (!enabled) {
        return {
            enabled: false,
            readOnly: false,
            startStep: null,
            workflowMode: originalWorkflowMode
        };
    }

    const activeDepartmentReview = ['help_department', 'request_department'].includes(originalWorkflowMode);

    return {
        enabled: true,
        readOnly: !activeDepartmentReview,
        startStep: 1,
        workflowMode: originalWorkflowMode.startsWith('request_')
            ? 'request_department'
            : 'help_department'
    };
};

export const stored_offer_can_resume = (offerDraft = {}) => {
    const offerStatus = `${offerDraft?.status || ''}`.trim().toLowerCase();
    const statusline = Array.isArray(offerDraft?.statusline) && offerDraft.statusline.length
        ? offerDraft.statusline
        : Array.isArray(offerDraft?.timeline) ? offerDraft.timeline : [];
    const lastTimelineStatus = `${statusline[statusline.length - 1]?.status || ''}`.trim().toLowerCase();
    const effectiveStatus = offerStatus || lastTimelineStatus;
    const resumableStatuses = [
        '',
        'draft',
        'ready_for_review',
        'review',
        'request_help',
        'requested_help'
    ];

    if (offerDraft?.deleted) {
        return false;
    }

    return resumableStatuses.includes(effectiveStatus);
};
